import {
    Card,
    CardContent,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Typography,
    IconButton,
    Tooltip,
    Button,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import b64toBlob from 'b64-to-blob';
import React, { useEffect, useState, useRef } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { parentUrl } from '../../../../utils/constants';
import { localDateFormat } from '../../../../utils/timeFormat';
import { RootState } from '../../../../reducers';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../store/store';
import WidgetLoading from '../../Shared/WidgetLoading';
import Authorization from '../../../../utils/Authorization';
import { WidgetTimeFilterEnum } from '../../../../Enum/WidgetTimeFilterEnum';
import { fetchPatientsAdmittedInPastDays, exportPatientsAdmittedInPastDays } from '../../../Widgets/WidgetService';
import TeamDropdown from '../../../Shared/TeamDropdown';
import { fetchTeams } from '../../../../store/careTeams.slice';

const ERAdmissions = (props) => {
    const dispatch = useAppDispatch();
    const authData = new Authorization();
    const ref = useRef(null);
    const { careTeams: teams } = useSelector((state: RootState) => state.careTeams);
    const [erAdmissions, setERAdmissions] = useState([]);
    const [selectedTeam, setSelectedTeam] = useState(authData.getDefaultTeamId(props.hasElevatedFilterAccess ? 'All' : ''));
    const [isLoading, setIsLoading] = useState(false);
    const [selectedFilter, setSelectedFilter] = useState<WidgetTimeFilterEnum>(WidgetTimeFilterEnum.Last15Days);
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [show, setShow] = useState(false);
    const [showInActiveTeams, setShowInActiveTeams] = useState(false);
    const [downLoading, setDownLoading] = useState<boolean>(false);

    const dayFilterList = [
        { Id: WidgetTimeFilterEnum.Last15Days, Value: WidgetTimeFilterEnum.Last15DaysDescription },
        { Id: WidgetTimeFilterEnum.Last30Days, Value: WidgetTimeFilterEnum.Last30DaysDescription },
        { Id: WidgetTimeFilterEnum.Last60Days, Value: WidgetTimeFilterEnum.Last60DaysDescription },
        { Id: WidgetTimeFilterEnum.Custom, Value: WidgetTimeFilterEnum.CustomDescription },
    ];

    useEffect(() => {
        dispatch(fetchTeams(true));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleClick = (event) => {
        setShow(!show);
    };

    const openDocumentHandler = (memberId: string) => {
        window.open(`${parentUrl}provider/members-list/member-chart/${memberId}/hie`, '_blank');
    };

    useEffect(() => {
        if (selectedFilter !== WidgetTimeFilterEnum.Custom || (Boolean(fromDate) && Boolean(toDate))) {
            setIsLoading(true);
            const changeFilter = async () => {
                setIsLoading(true);
                let dateFrom = Boolean(fromDate) && selectedFilter === WidgetTimeFilterEnum.Custom ? new Date(fromDate).toISOString() : '';
                let dateTo = Boolean(toDate) && selectedFilter === WidgetTimeFilterEnum.Custom ? new Date(toDate).toISOString() : '';
                const response = await fetchPatientsAdmittedInPastDays(selectedTeam, selectedFilter, dateFrom, dateTo, showInActiveTeams);
                setERAdmissions(response);
                setIsLoading(false);
            };
            changeFilter();
        }

        if (selectedFilter !== WidgetTimeFilterEnum.Custom && Boolean(fromDate) && Boolean(toDate)) {
            setFromDate(null);
            setToDate(null);
        }
    }, [selectedTeam, setERAdmissions, setIsLoading, selectedFilter, fromDate, toDate, showInActiveTeams]);

    const changeFromDate = (date: any) => {
        setFromDate(date);
        let dateInfo = new Date(date);
        if (!Boolean(toDate) || new Date(toDate) <= dateInfo) {
            let endDate = dateInfo.setDate(dateInfo.getDate() + 30);
            setToDate(endDate);
        }
    };

    const changeToDate = (date: any) => {
        setToDate(date);
        let dateInfo = new Date(date);
        if (!Boolean(fromDate) || new Date(fromDate) >= dateInfo) {
            let startDate = dateInfo.setDate(dateInfo.getDate() - 30);
            setFromDate(startDate);
        }
    };

    const exportPatientsAdmittedInPastDay = async () => {
        setDownLoading(true);
        let dateFrom = Boolean(fromDate) && selectedFilter === WidgetTimeFilterEnum.Custom ? new Date(fromDate).toISOString() : '';
        let dateTo = Boolean(toDate) && selectedFilter === WidgetTimeFilterEnum.Custom ? new Date(toDate).toISOString() : '';
        const response = await exportPatientsAdmittedInPastDays(selectedTeam, selectedFilter, dateFrom, dateTo, showInActiveTeams);
        let blob = b64toBlob(response.base64String, 'text/csv');
        let url = window.URL.createObjectURL(blob);
        let link = document.createElement('a');
        link.href = url;
        var date = new Date();
        var mm = date.getMonth() + 1; // getMonth() is zero-based
        var dd = date.getDate();
        var dateInfo = [(mm > 9 ? '' : '0') + mm, (dd > 9 ? '' : '0') + dd, date.getFullYear()].join('');
        link.setAttribute('download', `ERAdmissions - ${dateInfo}.xlsx`);
        link.click();
        link.remove();
        setDownLoading(false);
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Grid size={{ xs: 12, lg: 6 }}>
                <Paper className="smartdashboard-widget" elevation={5} sx={{ height: 'calc(100vh - 140px)', maxHeight: '650px', display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
                    <Grid container spacing={2} paddingLeft={2} paddingRight={2}>
                        <Grid size={12} textAlign="center">
                            <Typography display={'inline-block'} variant="h6" marginRight={1} color={'primary'}>
                                ER Admissions
                            </Typography>
                            <Typography display={'inline-block'} variant="body1" color={'primary'} ref={ref}>
                                &#40;{erAdmissions.length}&#41;
                                <Tooltip
                                    open={show}
                                    title={
                                        <div>
                                            <Typography variant="caption">Members in ER admissions in date range</Typography>
                                        </div>
                                    }
                                    PopperProps={{ style: { zIndex: 1000 } }}
                                >
                                    <IconButton onClick={handleClick}>
                                        <InfoIcon sx={{ width: '18px', height: '18px' }} />
                                    </IconButton>
                                </Tooltip>
                            </Typography>
                        </Grid>
                        {props.hasElevatedFilterAccess && (
                            <Grid size={2.5} marginTop={2}>
                                <TeamDropdown
                                    teams={teams}
                                    selectedTeam={selectedTeam}
                                    onChange={(id) => setSelectedTeam(id)}
                                    showInactive={showInActiveTeams}
                                    onToggleInactive={setShowInActiveTeams}
                                />
                            </Grid>
                        )}
                        <Grid size={2.5} marginTop={2}>
                            <FormControl size="small" fullWidth>
                                <InputLabel>Date Filter</InputLabel>
                                <Select
                                    value={selectedFilter}
                                    label="Date Filter"
                                    name="Date"
                                    onChange={(e) => setSelectedFilter(parseInt(e.target.value as any))}
                                >
                                    {dayFilterList.map((date) => (
                                        <MenuItem key={date.Id} value={date.Id}>
                                            {date.Value}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        {selectedFilter === WidgetTimeFilterEnum.Custom ? (
                            <React.Fragment>
                                <Grid size={{ md: 2.5 }} marginTop={2}>
                                    <DesktopDatePicker
                                        label="From Date"
                                        value={Boolean(fromDate) ? fromDate : null}
                                        minDate={new Date('2017-01-01')}
                                        onChange={(newValue) => changeFromDate(newValue)}
                                        slotProps={{ textField: { fullWidth: true, size: "small" } }}
                                    />
                                </Grid>
                                <Grid size={{ md: 2.5 }} marginTop={2}>
                                    <DesktopDatePicker
                                        label="To Date"
                                        value={Boolean(toDate) ? toDate : null}
                                        minDate={new Date('2017-01-01')}
                                        onChange={(newValue) => changeToDate(newValue)}
                                        slotProps={{ textField: { fullWidth: true, size: "small" } }}
                                    />
                                </Grid>
                                <Grid size={2} maxHeight="55px" marginTop="18px">
                                    <Button
                                        style={{ minWidth: '120px', marginLeft: 'auto' }}
                                        onClick={() => exportPatientsAdmittedInPastDay()}
                                        variant="contained"
                                        color="success"
                                        disabled={downLoading || erAdmissions?.length === 0}
                                    >
                                        {downLoading ? 'Exporting..' : 'Export'}
                                    </Button>
                                </Grid>
                            </React.Fragment>
                        ) : (
                            <>
                                <Grid size={5}></Grid>
                                <Grid size={2} maxHeight="55px" marginTop="18px">
                                    <Button
                                        style={{ minWidth: '120px', marginLeft: 'auto' }}
                                        onClick={() => exportPatientsAdmittedInPastDay()}
                                        variant="contained"
                                        color="success"
                                        disabled={downLoading || erAdmissions?.length === 0}
                                    >
                                        {downLoading ? 'Exporting..' : 'Export'}
                                    </Button>
                                </Grid>
                            </>
                        )}
                        <Grid
                            container
                            spacing={2}
                            sx={{
                                overflowY: 'auto',
                                position: 'relative',
                                height: 'calc(100vh - 285px)', 
                                maxHeight: '470px' 
                            }}
                        >
                            {isLoading && <WidgetLoading />}
                            {erAdmissions.length > 0 ? (
                                erAdmissions.map((member) => (
                                    <Grid key={member.Id} size={6}>
                                        {/* 6 / 12 = 2 cards per row */}
                                        <Card
                                            variant="outlined"
                                            onClick={() => openDocumentHandler(member.MemberId)}
                                            sx={{
                                                height: '100%',
                                                boxShadow: 0,
                                                ':hover': {
                                                    cursor: 'pointer',
                                                    boxShadow: 10,
                                                },
                                            }}
                                        >
                                            <CardContent
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'flex-start',
                                                    justifyContent: 'space-between',
                                                }}
                                            >
                                                <Grid container spacing={2}>
                                                    <Grid size={6}>
                                                        <Typography color="text.secondary" variant="body2">
                                                            Name
                                                        </Typography>
                                                        <Typography fontWeight="bold" variant="body2">
                                                            {`${member.MemberFirstName} ${member.MemberLastName}`}
                                                        </Typography>
                                                    </Grid>

                                                    <Grid size={6}>
                                                        <Typography color="text.secondary" variant="body2">
                                                            DOB
                                                        </Typography>
                                                        <Typography fontWeight="bold" variant="body2">
                                                            {localDateFormat(new Date(member.MemberDOB))}
                                                        </Typography>
                                                    </Grid>

                                                    <Grid size={12}>
                                                        <Typography color="text.secondary" variant="body2">
                                                            Location
                                                        </Typography>
                                                        <Typography fontWeight="bold" variant="body2">
                                                            {member.Location}
                                                        </Typography>
                                                    </Grid>

                                                    <Grid size={6}>
                                                        <Typography color="text.secondary" variant="body2">
                                                            Admission Date
                                                        </Typography>
                                                        <Typography fontWeight="bold" variant="body2">
                                                            {localDateFormat(new Date(member.AdmissionDate))}
                                                        </Typography>
                                                    </Grid>

                                                    <Grid size={6}>
                                                        <Typography color="text.secondary" variant="body2">
                                                            Discharge Date
                                                        </Typography>
                                                        <Typography fontWeight="bold" variant="body2">
                                                            {member.DischargeDate
                                                                ? localDateFormat(new Date(member.DischargeDate))
                                                                : '-'}
                                                        </Typography>
                                                    </Grid>
                                                </Grid>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))
                            ) : (
                                <Grid size={12} textAlign="center">
                                    <Typography variant="h6">No Matching Records</Typography>
                                </Grid>
                            )}
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>
        </LocalizationProvider>
    );
};

export default ERAdmissions;
